import { vec3 } from 'gl-matrix';

export class Ray {
  public origin: vec3;
  public direction: vec3;
  public inverseDirection: vec3;

  constructor(near: vec3 | number[], far: vec3 | number[]) {
    this.origin = vec3.copy(vec3.create(), near);

    let direction = vec3.create();
    vec3.subtract(direction, far, near);
    vec3.normalize(direction, direction);

    this.direction = direction;
    this.inverseDirection = vec3.inverse(vec3.create(), direction);
  }

  // public getFaceArrayIntersection(faces: Face[]): { face: Face, intersection: vec3 } {
  //
  //     for (let face of faces) {
  //         let intersection = this.getFaceIntersection(face);
  //         if (intersection) {
  //             return {
  //                 face: face,
  //                 intersection: intersection
  //             }
  //         }
  //     }
  //
  //     return null;
  // }

  // public getFaceIntersection(face: Face) {
  //     return this.getPlaneIntersection(
  //         face.vertices[0].position,
  //         face.vertices[1].position,
  //         face.vertices[2].position
  //     );
  // }

  // Returns a vector of the intersection
  public getPlaneIntersection(
    vecA: vec3 | number[],
    vecB: vec3 | number[],
    vecC: vec3 | number[],
  ): vec3 | null {
    // http://geomalgorithms.com/a06-_intersect-2.html
    // Based on magic (code) from:
    // http://www.lighthouse3d.com/tutorials/maths/ray-triangle-intersection/

    let edgeA = vec3.subtract(vec3.create(), vecB, vecA);
    let edgeB = vec3.subtract(vec3.create(), vecC, vecA);

    let h = vec3.cross(vec3.create(), this.direction, edgeB);
    let a = vec3.dot(edgeA, h);

    if (a > -0.00001 && a < 0.00001) {
      return null;
    }

    let f = 1 / a;
    let s = vec3.subtract(vec3.create(), this.origin, vecA);
    let u = f * vec3.dot(s, h);

    if (u < 0.0 || u > 1.0) {
      return null;
    }

    let q = vec3.cross(vec3.create(), s, edgeA);
    let v = f * vec3.dot(this.direction, q);

    if (v < 0.0 || u + v > 1.0) {
      return null;
    }

    let t = f * vec3.dot(edgeB, q);
    if (t > 0.00001) {
      // ray intersection
      return vec3.add(
        vec3.create(),
        this.origin,
        vec3.multiply(vec3.create(), this.direction, [t, t, t]),
      );
    } else {
      return null;
    }
  }

  /**
   * Ray vs infinite plane intersection.
   * Returns the world-space hit point, or null if the ray is parallel to or facing away from the plane.
   */
  public intersectPlane(planeNormal: vec3 | number[], planePoint: vec3 | number[]): vec3 | null {
    const denom = vec3.dot(planeNormal, this.direction);
    if (Math.abs(denom) < 0.00001) return null;
    const diff = vec3.subtract(vec3.create(), planePoint, this.origin);
    const t = vec3.dot(diff, planeNormal) / denom;
    if (t < 0) return null;
    return vec3.scaleAndAdd(vec3.create(), this.origin, this.direction, t);
  }

  public getSphereDistance(center: vec3 | number[], radius: number): number | null {
    const oc = vec3.subtract(vec3.create(), this.origin, center);
    const a = vec3.dot(this.direction, this.direction);
    const b = 2 * vec3.dot(oc, this.direction);
    const c = vec3.dot(oc, oc) - radius * radius;
    const discriminant = b * b - 4 * a * c;
    if (discriminant < 0) return null;
    const t = (-b - Math.sqrt(discriminant)) / (2 * a);
    return t > 0 ? t : null;
  }

  /**
   * Ray-capsule intersection. A capsule is a line segment (a→b) expanded by radius.
   * Returns distance along the ray to the entry point, or null if no hit.
   */
  public getCapsuleDistance(a: vec3 | number[], b: vec3 | number[], radius: number): number | null {
    // Degenerate capsule (endpoints coincide) → sphere
    const abx = (b[0] as number) - (a[0] as number);
    const aby = (b[1] as number) - (a[1] as number);
    const abz = (b[2] as number) - (a[2] as number);
    const abLenSq = abx * abx + aby * aby + abz * abz;
    if (abLenSq < 0.0001) {
      return this.getSphereDistance(a, radius);
    }

    // Find closest point on capsule axis to the ray.
    // Parameterize: ray point = O + s*D, capsule point = A + t*(B-A), t ∈ [0,1]
    const ox = this.origin[0] - (a[0] as number);
    const oy = this.origin[1] - (a[1] as number);
    const oz = this.origin[2] - (a[2] as number);
    const dx = this.direction[0];
    const dy = this.direction[1];
    const dz = this.direction[2];

    const dDotAb = dx * abx + dy * aby + dz * abz;
    const oDotAb = ox * abx + oy * aby + oz * abz;
    const dDotD = dx * dx + dy * dy + dz * dz; // ~1 since normalized
    const oDotD = ox * dx + oy * dy + oz * dz;

    // Solve for closest approach: 2x2 system
    // s*dDotD - t*dDotAb = -oDotD
    // s*dDotAb - t*abLenSq = -oDotAb
    let denom = dDotD * abLenSq - dDotAb * dDotAb;
    let s: number, t: number;

    if (Math.abs(denom) < 1e-10) {
      // Ray parallel to capsule axis — pick t=0, solve s
      t = 0;
      s = -oDotD / dDotD;
    } else {
      s = (dDotAb * oDotAb - abLenSq * oDotD) / denom;
      t = (dDotD * oDotAb - dDotAb * oDotD) / denom;
    }

    // Clamp t to [0,1] (capsule segment), re-derive s
    if (t < 0) {
      t = 0;
      s = -oDotD / dDotD;
    } else if (t > 1) {
      t = 1;
      s = (dDotAb - oDotD) / dDotD;
    }

    // Clamp s >= 0 (ray is forward), re-derive t
    if (s < 0) {
      s = 0;
      t = oDotAb / abLenSq;
      if (t < 0) t = 0;
      else if (t > 1) t = 1;
    }

    // Closest point on capsule axis
    const cx = (a[0] as number) + t * abx;
    const cy = (a[1] as number) + t * aby;
    const cz = (a[2] as number) + t * abz;

    // Test ray against sphere centered at that point
    return this.getSphereDistance(vec3.fromValues(cx, cy, cz), radius);
  }

  public intersectsBounds(min: vec3 | number[], max: vec3 | number[]): boolean {
    // This math is beyond me, credit goes to these guys:
    // https://tavianator.com/fast-branchless-raybounding-box-intersections/
    // https://www.cs.utah.edu/~awilliam/box/box.pdf
    // http://www.scratchapixel.com/lessons/3d-basic-rendering/minimal-ray-tracer-rendering-simple-shapes/ray-box-intersection

    var orig = this.origin;
    var dir = this.direction;

    var tmin = (min[0] - orig[0]) / dir[0];
    var tmax = (max[0] - orig[0]) / dir[0];

    if (tmin > tmax) {
      var tmp = tmin;
      tmin = tmax;
      tmax = tmp;
    }

    var tymin = (min[1] - orig[1]) / dir[1];
    var tymax = (max[1] - orig[1]) / dir[1];

    if (tymin > tymax) {
      var tmp = tymin;
      tymin = tymax;
      tymax = tmp;
    }

    if (tmin > tymax || tymin > tmax) {
      return false;
    }

    if (tymin > tmin) {
      tmin = tymin;
    }

    if (tymax < tmax) {
      tmax = tymax;
    }

    var tzmin = (min[2] - orig[2]) / dir[2];
    var tzmax = (max[2] - orig[2]) / dir[2];

    if (tzmin > tzmax) {
      var tmp = tzmin;
      tzmin = tzmax;
      tzmax = tmp;
    }

    if (tmin > tzmax || tzmin > tmax) {
      return false;
    }

    if (tzmin > tmin) {
      tmin = tzmin;
    }

    if (tzmax < tmax) {
      tmax = tzmax;
    }

    return true;
  }

  // https://github.com/stackgl/ray-aabb-intersection/blob/master/index.js
  public getBoundsIntersection(min: vec3 | number[], max: vec3 | number[]): vec3 | null {
    let distance = this.getBoundsDistance(min, max);
    if (distance === Infinity) {
      return null;
    } else {
      let result = vec3.create();
      for (let i = 0; i < 3; i++) {
        result[i] = this.origin[i] + this.direction[i] * distance;
      }
      return result;
    }
  }

  // https://github.com/stackgl/ray-aabb-intersection/blob/master/index.js
  public getBoundsDistance(min: vec3 | number[], max: vec3 | number[]): number {
    let lo = -Infinity;
    let hi = +Infinity;

    for (let i = 0; i < 3; i++) {
      let dimLo = (min[i] - this.origin[i]) / this.direction[i];
      let dimHi = (max[i] - this.origin[i]) / this.direction[i];

      if (dimLo > dimHi) {
        let tmp = dimLo;
        dimLo = dimHi;
        dimHi = tmp;
      }

      if (dimHi < lo || dimLo > hi) {
        return Infinity;
      }

      if (dimLo > lo) {
        lo = dimLo;
      }
      if (dimHi < hi) {
        hi = dimHi;
      }
    }
    return lo > hi ? Infinity : lo;
  }
}

export interface IntersectObject {
  canIntersectRay(): boolean;
  intersectsRay(ray: Ray): boolean;
}

export type RayTraceResult = {
  ray: Ray;
  object: any;
  intersection: any;
  distance: number;
};

/**
 * Compute the best plane normal for axis-constrained dragging.
 * The plane contains the drag axis and faces the camera as much as possible.
 * Falls back to a perpendicular world axis when the camera looks along the drag axis.
 */
export function computeDragPlaneNormal(axisDir: vec3, cameraForward: vec3): vec3 {
  const temp = vec3.cross(vec3.create(), axisDir, cameraForward);

  if (vec3.length(temp) < 0.001) {
    const ax = Math.abs(axisDir[0]);
    const ay = Math.abs(axisDir[1]);
    const az = Math.abs(axisDir[2]);
    const fallback =
      ax <= ay && ax <= az
        ? vec3.fromValues(1, 0, 0)
        : ay <= az
          ? vec3.fromValues(0, 1, 0)
          : vec3.fromValues(0, 0, 1);
    vec3.cross(temp, axisDir, fallback);
  }

  const normal = vec3.cross(vec3.create(), axisDir, temp);
  vec3.normalize(normal, normal);
  return normal;
}

/**
 * Project a world point onto a line (origin + t * direction), returning the scalar t.
 */
export function projectOntoAxis(point: vec3, origin: vec3, axisDir: vec3): number {
  const diff = vec3.subtract(vec3.create(), point, origin);
  return vec3.dot(diff, axisDir);
}
